// =============================================================================
// useRealtimeSubscription — reusable Supabase realtime helper
// =============================================================================
// Wraps supabase.channel + postgres_changes with:
//   - Debounced refetch (default 400ms) — unika N re-renderow przy batch opsach
//   - isLive status (true gdy SUBSCRIBED, false inaczej) — do <LiveIndicator>
//   - Auto-cleanup przy unmount / zmianie deps
//
// Usage:
//   const { isLive } = useRealtimeSubscription({
//     channel: `entities_${mapId}`,
//     table: "thematic_entities",
//     filter: mapId ? `map_id=eq.${mapId}` : undefined,
//     enabled: Boolean(mapId && user),
//     onChange: fetchEntities,
//   });
//
// Returns `isLive` so hook/component consumer can expose it and <LiveIndicator>
// renders a green/gray dot. No optimistic merge here — caller decides pattern
// (refetch vs setState from payload.new).

import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

// Monotonic suffix — Phoenix dopuszcza tylko JEDEN join per topic per socket.
// Gdy dwa komponenty (np. ThematicMapDetailPage + ContentEnginePanel) subskrybują
// ten sam bazowy kanał, serwer zabija starszą subskrypcję (duplicate join), a po
// unmount drugiej removeChannel zwalnia topic → nikt nie słucha → stale UI do F5.
// Unikalna nazwa per subscribe eliminuje kolizję (wiele topiców może słuchać tej
// samej tabeli) oraz race przy szybkim leave→join na tym samym topicu (StrictMode).
let channelSeq = 0;

interface RealtimeSubOptions {
  /** Bazowa nazwa kanału (czytelność w debugach). Unikalność globalna jest
   * dokładana wewnętrznie (suffix), więc wiele instancji tego samego hooka
   * może bezpiecznie współistnieć. */
  channel: string;
  /** Table name in public schema. */
  table: string;
  /** Optional filter like "map_id=eq.UUID". */
  filter?: string;
  /** Whether subscription should be active. Defaults to true. */
  enabled?: boolean;
  /** Called after debounced window closes. */
  onChange: () => void;
  /** Debounce window in ms. Defaults to 400. */
  debounceMs?: number;
}

interface RealtimeSubResult {
  /** True iff the channel is in SUBSCRIBED state. */
  isLive: boolean;
}

export function useRealtimeSubscription({
  channel,
  table,
  filter,
  enabled = true,
  onChange,
  debounceMs = 400,
}: RealtimeSubOptions): RealtimeSubResult {
  const [isLive, setIsLive] = useState(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!enabled) {
      setIsLive(false);
      return;
    }

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleRefetch = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        onChangeRef.current();
        debounceTimer = null;
      }, debounceMs);
    };

    const sub = supabase
      .channel(`${channel}__${++channelSeq}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table, ...(filter ? { filter } : {}) },
        scheduleRefetch,
      )
      .subscribe((status) => {
        setIsLive(status === "SUBSCRIBED");
      });

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(sub);
      setIsLive(false);
    };
  }, [channel, table, filter, enabled, debounceMs]);

  return { isLive };
}
